import { StrictMode } from 'react';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { App } from './App';
import './design-system/fonts.css';
import './design-system/tokens.css';
import './design-system/reset.css';
import './design-system/utilities.css';
import './design-system/tailwind.css';
import { bootstrapTraceFromCookie } from './modules/telemetry';
import { initTracking, track, trackingPage } from '../../shared-web/track';
import { initPageEngagement } from '../../shared-web/page-engagement';
import { readBootstrap } from './types/bootstrap';
import { parseRoute } from './modules/ember/router';
import { syncInternalVisitorMarker } from '../../shared-web/internal-visitor';
import { emberPageSurface } from '../../shared/analytics/ember-page-surface.js';
import { getOs, isInAppWebview } from '../../shared-web/ua-detect';
import { initInstallCompletion, isStandaloneDisplay } from '../../shared-web/install-guide.js';
import { initStaleBundleReload } from '../../shared-web/stale-bundle-reload';
import { toast } from './design-system/components';

// Capture `beforeinstallprompt` as EARLY as possible (module eval, before React
// mounts). Chromium fires it once at load — often before EmberHome's own
// listener attaches — so without this the deferred prompt is lost and the
// install affordance has no native path. Stash it on window for the component.
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as unknown as { __oriInstallPrompt?: Event }).__oriInstallPrompt = e;
});

// Pull the trace_id from the one-shot ori_trace cookie BEFORE any
// React code runs, so the first apiFetch (likely SessionProvider's
// /api/me) carries X-Ori-Trace-Id and lands in the correct
// correlation context.
bootstrapTraceFromCookie();
// Internal-visitor marker (`?internal=1`), applied before the first beacon so
// the page load that turns it on is itself excluded. Twin of the marketing
// bundle's call — a marker set on either side has to hold on both.
syncInternalVisitorMarker();

// page.rendered — the bundle is alive on this route; also installs the
// delegated ui.clicked listener and the page.error handlers.
const boot = readBootstrap();
// The page's route name — the one page.navigated and page.engaged carry. The
// product SPA's bootstrap names only the app ('ember'); its page is read off
// the location the way the router will. Other shells name their page.
const bootRoute = boot.route === 'ember' ? parseRoute(window.location.pathname).name : boot.route;
// mode/tab are the SPA's page-identity query keys (EmberApp's route effect
// treats the same two as identity): a document that BOOTS on /talk?mode=chat
// or a ?tab= link is already on that page, and no page.navigated will ever
// say so — this render row is its only page-level record.
const bootQuery = new URLSearchParams(window.location.search);
initTracking({
  surface: emberPageSurface(window.location.pathname),
  route: bootRoute,
  renderProps: {
    route: bootRoute,
    mode: bootQuery.get('mode') || undefined,
    tab: bootQuery.get('tab') || undefined,
  },
});

// A page running an older bundle than the live deploy reloads itself onto it
// (see the module doc). Only a recording is told first.
initStaleBundleReload(boot.appVersion, {
  warn: (seconds, reloadNow) => toast.info(`Updating in ${seconds} seconds`, {
    duration: seconds,
    action: { label: 'Update now', onPress: reloadNow },
  }),
});

// One row per full document launch — SPA route changes do not repeat it. This
// is the denominator for launch-source reporting: an installed Home Screen
// window, an app-owned in-app browser, or an ordinary system browser tab.
// Keep it separate from install.completed: installation is a lifecycle fact;
// app.opened is deliberately countable every time the bundle starts.
//
// How the document was arrived at (2026-10-28, a person on Meet people was taken / → /friends → a place → /friends in
// one tab, each a fresh document with no click on any page, and nothing said how): `nav` is the browser's own account
// of the load (navigate | reload | back_forward | prerender), `from` the page of this site it was left from — absent
// when it came from no page or another site (typed, a bookmark, a search result).
function documentNav(): string | undefined {
  try {
    return (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type || undefined;
  } catch {
    return undefined;
  }
}
function referrerPath(): string | undefined {
  try {
    const ref = document.referrer ? new URL(document.referrer) : null;
    return ref && ref.origin === window.location.origin ? ref.pathname : undefined;
  } catch {
    return undefined;
  }
}
track('app.opened', {
  launch_mode: isStandaloneDisplay(window)
    ? 'home_screen'
    : (isInAppWebview() ? 'in_app_browser' : 'browser'),
  os: getOs(),
  route: boot.route,
  nav: documentNav(),
  from: referrerPath(),
});
// install.completed — a standalone launch (once per session) or Chromium's
// appinstalled — observed from the bundle's start on any page, not only where
// the Add to Home Screen button is mounted.
initInstallCompletion({ os: getOs(), win: window });

// page.scrolled milestones + page.engaged summary — the abandonment half of
// the funnel: a visitor who leaves without touching anything emits no click
// and would otherwise be invisible.
//
// Both are per PAGE VISIT, not per tab load: EmberApp calls endPageVisit() on
// every client route change, so reading three Requests off the homepage is
// four visits, each with its own clock, its own depth and its own milestones.
// The page each beacon belongs to is whatever the router last declared
// (trackingPage()) — at a route change the flush runs BEFORE the declaration,
// so a summary is stamped with the page being left, not the one being entered.
initPageEngagement({
  onScrollDepth: (depth) => {
    let t: number | null = null;
    try { t = Math.round(performance.now()); } catch { /* t stays null */ }
    track('page.scrolled', { depth, t_ms: t, route: trackingPage().route });
  },
  onEngagement: (s) => track('page.engaged', {
    route: trackingPage().route,
    visit_id: s.visit_id,
    dwell_ms: s.dwell_ms,
    visible_ms: s.visible_ms,
    max_scroll_pct: s.max_scroll_pct,
    clicks: s.clicks,
  }),
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found');
}

const app = (
  <StrictMode>
    <App />
  </StrictMode>
);

// Public indexable routes arrive with React-rendered HTML in #root. Reuse that
// DOM and attach behavior instead of clearing and rebuilding the first screen.
// Private/client-only routes retain the ordinary createRoot path.
const canHydrateHomepage = rootEl.hasChildNodes()
  && boot.route === 'ember'
  && boot.ssr?.page === 'home'
  && window.location.pathname === '/';

if (canHydrateHomepage) {
  hydrateRoot(rootEl, app);
} else {
  createRoot(rootEl).render(app);
}

// Register the minimal pass-through service worker so Chromium (Android /
// desktop) treats Ember as installable and offers "Add to Home Screen". Caches
// nothing; best-effort — registration failure is non-fatal.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => { /* non-fatal */ });
  });
}
